/** @jsx jsx */
import { useFocusRing } from '@react-aria/focus';
import { AriaListBoxOptions, useListBox, useOption } from '@react-aria/listbox';
import { mergeProps } from '@react-aria/utils';
import { ListState } from '@react-stately/list';
import { jsx } from '@theme-ui/core';
import * as React from 'react';
import type { Node } from '@react-types/shared';

interface ListBoxProps extends AriaListBoxOptions<unknown> {
  listBoxRef?: React.RefObject<HTMLUListElement>;
  state: ListState<unknown>;
  width?: number;
}

export function ListBox(props: ListBoxProps) {
  // Get props for the listbox element
  let ref = React.useRef(null);
  const { state, listBoxRef = ref, ...restProps } = props;

  let { listBoxProps } = useListBox(restProps, state, listBoxRef);

  return (
    <ul
      {...listBoxProps}
      ref={listBoxRef}
      sx={{
        margin: 0,
        py: 1,
        px: 0,
        listStyle: 'none',
        overflow: 'auto',
        overflowX: 'hidden',
        maxHeight: '250px',
        minWidth: props?.width ? props?.width : undefined
      }}
    >
      {[...state.collection].map((item) => (
        <Option key={item.key} item={item} state={state} />
      ))}
    </ul>
  );
}

interface OptionProps {
  item: Node<unknown>;
  state: ListState<unknown>;
}

function Option({ item, state }: OptionProps) {
  // Get props for the option element
  let ref = React.useRef(null);
  let { optionProps, isSelected } = useOption({ key: item.key }, state, ref);

  // Determine whether we should show a keyboard
  // focus ring for accessibility
  let { isFocusVisible, focusProps } = useFocusRing();
  let color: string = null!;

  return (
    <li
      {...mergeProps(optionProps, focusProps)}
      ref={ref}
      style={{
        color
      }}
      data-item-selected={isSelected}
      data-item-highlighted={isFocusVisible || undefined}
      sx={{
        variant: 'menuItems.default'
      }}
    >
      {item.rendered}
    </li>
  );
}
